import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/components/ui/use-toast";
import { useCreateMerchValue, RiskMerchValue } from "@/lib/api/risk/merchValues";
import { MerchValuesForm } from "./MerchValuesForm";
import { MerchValueFormValues } from "./form-schema";
import { formToCreateApi } from "./transform";

interface CreateMerchValueDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Pre-fill filters from the parent page */
  defaults?: Partial<MerchValueFormValues>;
  /** All existing merch value items (used for overlap detection) */
  existingItems: RiskMerchValue[];
}

export function CreateMerchValueDialog({
  open,
  onOpenChange,
  defaults,
  existingItems,
}: CreateMerchValueDialogProps) {
  const { toast } = useToast();
  const createMerchValue = useCreateMerchValue();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (values: MerchValueFormValues) => {
    setIsSubmitting(true);
    try {
      const payload = formToCreateApi(values);
      const result = await createMerchValue(payload);

      if (result.success) {
        toast({ title: "Success", description: "Merch value created successfully" });
        onOpenChange(false);
      } else {
        toast({
          title: "Error",
          description: result.message || "Failed to create merch value",
          variant: "destructive",
        });
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : "An error occurred";
      toast({
        title: "Error",
        description: message,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Create Merch Value</DialogTitle>
        </DialogHeader>
        <MerchValuesForm
          initialValues={defaults}
          isSubmitting={isSubmitting}
          onSubmit={handleSubmit}
          submitLabel="Create"
          existingItems={existingItems}
        />
      </DialogContent>
    </Dialog>
  );
}
